import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { uniq } from 'lodash';

import {
  Autocomplete,
  Box,
  createFilterOptions,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  GoToNextStep,
  GoBackOnSkills,
  StickyPanel,
  SelectableWrapper,
} from '../../../components';
import { useTechSkillsContext } from '../TechSkills';

import { userSelectors } from '../../../redux/user';
import {
  gamifiedResponseSelectors,
  gamifiedResponseSlice,
} from '../../../redux/gamifiedResponse';
import { skillsSelectors } from '../../../redux/skills';
import {
  clearNotificationAlert,
  notificationAlert,
} from '../../../redux/notifications';

import { logEvent } from 'src/services';
import { useFocusInputAfterAnimation, useScrollToTop } from '../../../hooks';
import { secondarySkillsMessage } from './secondarySkillsMessage';
import { getUserPrefferedName } from '../../../utils';
import { DEVELOPER_TYPES } from 'src/constants';

const SECONDARY_SKILL_MAX_LENGTH = 255;
const SECONDARY_SKILL_SET_MAX_LENGTH = 700;

const SORT_MODE = {
  RELATED_FIRST: 'RELATED_FIRST',
  ALL_AT_ONCE: 'ALL_AT_ONCE',
} as const;

type SortMode = (typeof SORT_MODE)[keyof typeof SORT_MODE];

const SecondarySkills: React.FC = () => {
  useScrollToTop();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(userSelectors.getIsUserPerformingAction);
  const userData = useAppSelector(userSelectors.getUserData)!;
  const skillsData = useAppSelector(skillsSelectors.getSkillsData);
  const lastGamifiedMessage = useAppSelector(
    gamifiedResponseSelectors.getGamifiedResponse,
  );
  const userTechStack = useAppSelector(skillsSelectors.getTechStack);
  const {
    handleSecondarySkillsChange,
    handleSubmitSecondarySkills,
    goBackToWorkArea,
    goBackToSpecialization,
    goBackToPrimarySkills,
  } = useTechSkillsContext();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [sortMode, setSortMode] = useState<SortMode>(SORT_MODE.RELATED_FIRST);

  // Selected primary skill names by user on previous step
  const selectedPrimarySkillNames = useMemo(
    () => userTechStack?.primarySkills ?? [],
    [userTechStack?.primarySkills],
  );
  // Selected secondary skill names by user
  const selectedSecondarySkillNames = useMemo(() => {
    const newArray: string[] = [];
    if (userTechStack?.secondarySkills) {
      newArray.push(...userTechStack?.secondarySkills);
    }
    if (userTechStack?.skillset) {
      newArray.push(...userTechStack?.skillset);
    }
    if (newArray.length) {
      return newArray;
    } else {
      return null;
    }
  }, [userTechStack?.secondarySkills, userTechStack?.skillset]);
  // Primary skill names without selected ones
  const primarySkillNames = useMemo(() => {
    const isFullStack =
      userTechStack?.specialization === DEVELOPER_TYPES.fullstack;
    const selectedSpecializations = isFullStack
      ? [DEVELOPER_TYPES.backend, DEVELOPER_TYPES.frontend]
      : [userTechStack?.specialization];

    return (
      skillsData.data.skills
        .filter(
          (skill) =>
            skill.type === 'primary' &&
            !selectedPrimarySkillNames.includes(skill.name) &&
            selectedSpecializations.includes(skill.specialization),
        )
        .map((skill) => skill.name)
        .sort((a, b) => a.localeCompare(b)) || []
    );
  }, [
    skillsData.data.skills,
    selectedPrimarySkillNames,
    userTechStack?.specialization,
  ]);
  // Secondary skill names
  const secondarySkillNames = useMemo(
    () =>
      skillsData.data.skills
        .filter((skill) => skill.type === 'secondary')
        .map((skill) => skill.name)
        .sort((a, b) => a.localeCompare(b)) || [],
    [skillsData.data.skills],
  );

  // Related skill names based on selected primary skills
  const relatedSkillNames = useMemo(() => {
    const isFullStack =
      userTechStack?.specialization === DEVELOPER_TYPES.fullstack;
    const selectedSpecializations = isFullStack
      ? [DEVELOPER_TYPES.backend, DEVELOPER_TYPES.frontend]
      : [userTechStack?.specialization];

    const selectedPrimarySkills = skillsData.data.skills.filter(
      (skill) =>
        selectedPrimarySkillNames.includes(skill.name) &&
        skill.type === 'primary' &&
        selectedSpecializations.includes(skill.specialization),
    );
    const relatedSkillNames = selectedPrimarySkills.reduce<string[]>(
      (acc, skill) => {
        if (skill.relatedSkills) {
          acc.push(...skill.relatedSkills);
        }
        return acc;
      },
      [],
    );

    return uniq([
      ...primarySkillNames.filter((name) => relatedSkillNames.includes(name)),
      ...secondarySkillNames.filter((name) => relatedSkillNames.includes(name)),
    ]);
  }, [
    skillsData.data.skills,
    selectedPrimarySkillNames,
    primarySkillNames,
    secondarySkillNames,
    userTechStack?.specialization,
  ]);
  // Other skill names that are not related to selected primary skills
  const otherSkillNames = useMemo(() => {
    return uniq([
      ...primarySkillNames.filter((name) => !relatedSkillNames.includes(name)),
      ...secondarySkillNames.filter(
        (name) => !relatedSkillNames.includes(name),
      ),
    ]);
  }, [relatedSkillNames, primarySkillNames, secondarySkillNames]);
  // All skill names to show without selected primary skills
  const allSkillNames = useMemo(() => {
    return uniq([...primarySkillNames, ...secondarySkillNames]);
  }, [primarySkillNames, secondarySkillNames]);

  const shouldShowRadio = relatedSkillNames.length > 0;
  const effectiveSortMode: SortMode = shouldShowRadio
    ? sortMode
    : SORT_MODE.ALL_AT_ONCE;

  useFocusInputAfterAnimation(inputRef);

  useEffect(() => {
    if (selectedSecondarySkillNames) {
      dispatch(gamifiedResponseSlice.actions.updateMessage(null));
    } else {
      if (!lastGamifiedMessage) {
        dispatch(
          gamifiedResponseSlice.actions.updateMessage(
            `Welcome back, ${getUserPrefferedName(userData)}!`,
          ),
        );
      }
    }

    logEvent('launchpod-secondary-skills-page-loaded');
  }, []);

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmitSecondarySkills();
  };

  const applyChange = (newValue: string[]) => {
    handleSecondarySkillsChange(newValue);
    dispatch(
      gamifiedResponseSlice.actions.updateMessage(
        secondarySkillsMessage({
          secondarySkillsSelected: newValue,
        }),
      ),
    );
  };

  const isWithinSkillSetLimit = (skills: string[]) =>
    skills.join(',').length <= SECONDARY_SKILL_SET_MAX_LENGTH;

  const notifySkillSetLimitReached = () => {
    dispatch(
      notificationAlert("You've added the maximum number of technologies", {
        variant: 'error',
        key: 'long-skillset',
      }),
    );
  };

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: string[] = [],
  ) => {
    dispatch(clearNotificationAlert('long-skillset'));
    if (newValue.length) {
      const lastValue = newValue[newValue.length - 1];
      if (lastValue.length > SECONDARY_SKILL_MAX_LENGTH) {
        dispatch(
          notificationAlert('Value too long', {
            variant: 'error',
            key: 'long-skillset',
          }),
        );
        return;
      }

      if (lastValue.includes("Add '")) {
        newValue[newValue.length - 1] = lastValue
          .replace('Add ', '')
          .replaceAll("'", '');
      }

      const isAddingSkill =
        newValue.length > (selectedSecondarySkillNames?.length ?? 0);
      if (isAddingSkill && !isWithinSkillSetLimit(newValue)) {
        notifySkillSetLimitReached();
        return;
      }
    }

    applyChange(newValue);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    valueSelected: string[] = [],
  ) => {
    if (event.key == ',') {
      event.preventDefault();
      event.stopPropagation();
      handleChange(event, [...valueSelected, event.target.value]);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const prevValues = selectedSecondarySkillNames?.length
      ? selectedSecondarySkillNames
      : [];
    const newValue = event.target.value.trim();
    if (newValue) {
      handleChange(event, [...prevValues, newValue]);
    }
  };

  const handleGridToggle = (skillName: string) => {
    const current = selectedSecondarySkillNames ?? [];
    const isRemoving = current.includes(skillName);
    const next = isRemoving
      ? current.filter((name) => name !== skillName)
      : [...current, skillName];

    if (!isRemoving && !isWithinSkillSetLimit(next)) {
      notifySkillSetLimitReached();
      return;
    }

    dispatch(clearNotificationAlert('long-skillset'));
    applyChange(next);
  };

  const renderSkillsGrid = (skills: string[]) => (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {skills.map((skill) => (
        <Box key={skill}>
          <SelectableWrapper
            value={skill}
            isSelected={selectedSecondarySkillNames?.includes(skill) ?? false}
            onClick={() => handleGridToggle(skill)}
          >
            <Typography noWrap>{skill}</Typography>
          </SelectableWrapper>
        </Box>
      ))}
    </Stack>
  );

  return (
    <>
      <GoBackOnSkills
        workArea={userTechStack?.workArea || ''}
        goBackToWorkArea={goBackToWorkArea}
        specialization={userTechStack?.specialization}
        goBackToSpecialization={goBackToSpecialization}
        primarySkills={userTechStack?.primarySkills}
        goBackToPrimarySkills={goBackToPrimarySkills}
      />
      <Typography variant="h2" data-testid="secondary-skills-layout">
        What other technologies
        <br />
        have you worked with?
      </Typography>
      <form onSubmit={handleSubmit}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Autocomplete
            multiple
            freeSolo
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            disableClearable
            value={selectedSecondarySkillNames ?? []}
            onChange={handleChange}
            onBlur={handleBlur}
            options={allSkillNames}
            filterOptions={(options, params) => {
              const filtered = createFilterOptions<string>()(
                options,
                params,
              ) as string[];

              if (params.inputValue !== '') {
                filtered.push(`Add '${params.inputValue}'`);
              }

              return filtered;
            }}
            filterSelectedOptions
            renderOption={(props, option) => (
              <Box
                key={option}
                component="li"
                {...props}
                padding="1rem 1.5rem!important"
                sx={(theme) => ({
                  backgroundColor: `${theme.palette.highlight.actionable}!important`,
                  borderBottom: '3px solid white',
                  '&:hover': {
                    backgroundColor: `${theme.palette.highlight.accent}!important`,
                  },
                  '&.Mui-focused': {
                    backgroundColor: `${theme.palette.highlight.accent}!important`,
                  },
                })}
              >
                {option}
              </Box>
            )}
            renderInput={(params) => {
              params.inputProps.onKeyDown = (e) =>
                handleKeyDown(e, selectedSecondarySkillNames || []);
              return (
                <TextField
                  {...params}
                  placeholder={
                    selectedSecondarySkillNames?.length
                      ? ''
                      : 'Select or add technologies and frameworks'
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon
                          sx={(theme) => ({
                            marginLeft: '1rem',
                            color: theme.palette.text.secondary,
                          })}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  inputRef={inputRef}
                />
              );
            }}
            sx={{ width: '100%', overflow: 'auto' }}
            onKeyUp={(e: KeyboardEvent) => e.stopPropagation()}
            disablePortal
          />
          {shouldShowRadio && (
            <Stack direction="row" spacing={1} height="fit-content">
              <SelectableWrapper
                value={SORT_MODE.RELATED_FIRST}
                selectedValue={sortMode}
                onClick={() => setSortMode(SORT_MODE.RELATED_FIRST)}
              >
                <Typography noWrap>Related first</Typography>
              </SelectableWrapper>
              <SelectableWrapper
                value={SORT_MODE.ALL_AT_ONCE}
                selectedValue={sortMode}
                onClick={() => setSortMode(SORT_MODE.ALL_AT_ONCE)}
              >
                <Typography noWrap>All at once</Typography>
              </SelectableWrapper>
            </Stack>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: '1rem' }}>
          Be honest about the technologies you have worked with!
        </Typography>

        <Box mt={4}>
          {effectiveSortMode === SORT_MODE.RELATED_FIRST ? (
            <>
              <Box mb={otherSkillNames.length > 0 ? 4 : 0}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {`${selectedPrimarySkillNames.join(', ')} related`}
                </Typography>
                {renderSkillsGrid(relatedSkillNames)}
              </Box>
              {otherSkillNames.length > 0 && (
                <Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    gutterBottom
                  >
                    Other
                  </Typography>
                  {renderSkillsGrid(otherSkillNames)}
                </Box>
              )}
            </>
          ) : (
            renderSkillsGrid(allSkillNames)
          )}
        </Box>

        <StickyPanel>
          <GoToNextStep
            isLoading={isLoading}
            isDisabled={false}
            textIdentifier={0}
            maxWidth={{ xs: '10rem', sm: 'initial' }}
          />
        </StickyPanel>
      </form>
    </>
  );
};

export default SecondarySkills;
